import React, { forwardRef, useEffect, useImperativeHandle } from 'react';

import { PaperPlaneIcon } from '@radix-ui/react-icons';
import { Callout, Flex, IconButton, Spinner } from '@raystack/apsara';

import { Chat } from '@src/clients/seeker/api';
import TextArea from '@src/components/TextArea/TextArea';
import { useCopilotSelector } from '@src/contexts';
import { Show } from '@src/hoc';
import { useCopilot } from '@src/hooks/useCopilot';

import styles from './style.module.css';

interface CopilotFooterProps {
  chatId?: Chat['id'];
}
interface CopilotFooterRef {
  setMessage: (newMessage: string) => void;
}
export const CopilotFooter = forwardRef<CopilotFooterRef, CopilotFooterProps>(
  (props, ref) => {
    const [message, setMessage] = React.useState<string>('');
    const isSending = useCopilotSelector(state => state.isSending);
    const isPolling = useCopilotSelector(state => state.isPolling);
    const resetMessageId = useCopilotSelector(state => state.resetMessageId);
    const isCreatingChat = useCopilotSelector(state => state.isCreatingChat);
    const createChatFailed = useCopilotSelector(
      state => state.createChatFailed
    );
    const msgFetchFailed = useCopilotSelector(state => state.msgFetchFailed);

    const { sendMessage } = useCopilot();
    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(event.target.value);
    };

    useImperativeHandle(ref, () => ({
      setMessage: (newMessage: string) => setMessage(newMessage)
    }));

    // Add event listener for Enter/Return key press
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Enter' || event.key === 'Return') {
          event.preventDefault(); // Prevents new line in text area
          if (message.trim().length > 0 && !isSending && !isPolling) {
            sendMessage({ message, setMessage });
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      // Cleanup event listener on unmount
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [message, isSending, isPolling]);

    return (
      <Flex className={styles.container}>
        <Show
          when={!createChatFailed}
          fallback={
            <Callout type='alert' width='100%'>
              An error occurred, please try again in sometime.
            </Callout>
          }
        >
          <Flex className={styles.searchContainer}>
            <TextArea
              className={styles.textarea}
              value={message}
              onChange={handleChange}
              disabled={!!resetMessageId && resetMessageId?.length > 0}
              placeholder={
                msgFetchFailed
                  ? "Ask me anything… (previous messages couldn't be loaded)"
                  : 'Search Aurora capabilities or query geospatial datasets…'
              }
              data-test-id='copilot-message'
            />

            <IconButton
              data-test-id='btn-send-message'
              onClick={() => sendMessage({ message, setMessage })}
              disabled={
                isSending ||
                isPolling ||
                (!!resetMessageId && resetMessageId?.length > 0) ||
                isCreatingChat ||
                message.trim().length == 0
              }
            >
              <Show
                when={isSending || isCreatingChat}
                fallback={<PaperPlaneIcon />}
              >
                <Spinner size={3} />
              </Show>
            </IconButton>
          </Flex>
        </Show>
      </Flex>
    );
  }
);

CopilotFooter.displayName = 'CopilotFooter';
